import { useState, useEffect, useCallback, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';
import { ANALYSIS_LIMITS } from '../config/pricing/limits';
import { PlanId } from '../config/pricing/types';

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

export interface SubscriptionStatus {
  plan: PlanId;
  status: 'active' | 'inactive' | 'trialing' | 'past_due' | 'canceled';
  analysisCount: number;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd?: boolean;
  organizationId?: string;
  licensesUsed?: number;
  maxLicenses?: number;
}

export interface OrganizationMembership {
  organizationId: string;
  organizationName: string;
  role: 'admin' | 'member';
  licenses_purchased: number;
  licenses_used: number;
}

interface UseSubscriptionResult {
  subscription: SubscriptionStatus | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  organization: OrganizationMembership | null;
  isOrganizationMember: boolean;
  organizationRole: 'admin' | 'member' | null;
}

// Enterprise default limit per license
const DEFAULT_ENTERPRISE_LIMIT = ANALYSIS_LIMITS.ENTERPRISE / 100; // 1000 per license

export function useSubscription(): UseSubscriptionResult {
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [organization, setOrganization] = useState<OrganizationMembership | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchingRef = useRef(false);

  const fetchSubscription = useCallback(async () => {
    // Prevent duplicate fetches
    if (fetchingRef.current) return;
    fetchingRef.current = true;

    try {
      setIsLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setSubscription(null);
        setOrganization(null);
        return;
      }

      // Simplified: Single query for organization membership
      const { data: memberData, error: memberError } = await supabase
        .from('organization_members')
        .select(`
          organization_id,
          role,
          analysis_count,
          organizations!inner(
            name,
            licenses_purchased,
            licenses_used
          )
        `)
        .eq('user_id', user.id)
        .maybeSingle();

      // Handle no membership (user needs onboarding)
      if (memberError?.code === 'PGRST116' || !memberData) {
        setOrganization(null);
        setSubscription({
          plan: 'enterprise',
          status: 'active', // Users without org still get active status
          analysisCount: 0,
        });
        return;
      }

      // Handle unexpected errors
      if (memberError) {
        console.warn('Organization membership fetch error:', memberError);
        setOrganization(null);
        setSubscription({
          plan: 'enterprise',
          status: 'active', // Default to active status for errors
          analysisCount: 0,
        });
        return;
      }

      // User has organization - get subscription details
      const { data: orgSubData } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('organization_id', memberData.organization_id)
        .maybeSingle();

      const orgInfo = Array.isArray(memberData.organizations)
        ? memberData.organizations[0]
        : memberData.organizations;

      // Set organization info
      setOrganization({
        organizationId: memberData.organization_id,
        organizationName: orgInfo.name,
        role: memberData.role,
        licenses_purchased: orgInfo.licenses_purchased || 100,
        licenses_used: orgInfo.licenses_used || 1,
      });

      // Set subscription info
      setSubscription({
        plan: 'enterprise',
        status: orgSubData?.status || 'active', // Default to 'active' for org members
        analysisCount: memberData.analysis_count || 0,
        currentPeriodEnd: orgSubData?.current_period_end,
        cancelAtPeriodEnd: orgSubData?.cancel_at_period_end,
        organizationId: memberData.organization_id,
        licensesUsed: orgInfo.licenses_used,
        maxLicenses: orgInfo.licenses_purchased,
      });

    } catch (err) {
      console.error('Error fetching subscription:', err);

      // Default to active state on error
      setOrganization(null);
      setSubscription({
        plan: 'enterprise',
        status: 'active', // Default to active - authenticated users have paid
        analysisCount: 0,
      });
      setError(null);
    } finally {
      setIsLoading(false);
      fetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  return {
    subscription,
    isLoading,
    error,
    refetch: fetchSubscription,
    organization,
    isOrganizationMember: !!organization,
    organizationRole: organization?.role || null,
  };
}

// Helper function to get analysis limit based on subscription
export function getAnalysisLimit(subscription: SubscriptionStatus | null): number {
  if (!subscription) {
    return DEFAULT_ENTERPRISE_LIMIT; // Default enterprise limit per license
  }

  // All authenticated users get full enterprise limit (1000 per license)
  // No trial limits - authentication means they've paid
  return DEFAULT_ENTERPRISE_LIMIT;
}

// Helper function to check if user can perform analysis
export function canPerformAnalysis(subscription: SubscriptionStatus | null): boolean {
  if (!subscription) return false;

  const limit = getAnalysisLimit(subscription);
  const used = subscription.analysisCount || 0;

  return used < limit;
}